import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileText,
  CheckCircle,
  Loader2,
  Download,
  Shield,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractSignaturePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({
    accepte_conditions: false,
    nom_signataire: "",
    prenom_signataire: "",
    qualite_signataire: "Proprietaire",
  });
  
  useEffect(() => {
    checkContractStatus();
  }, []);
  
  const checkContractStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Check if already signed
      const statusRes = await axios.get(`${API_URL}/api/contract/status`, { headers });
      
      if (statusRes.data.has_signed) {
        setSigned(true);
        setSignatureData(statusRes.data.signature);
      } else {
        // Get contract template
        const templateRes = await axios.get(`${API_URL}/api/contract/template`, { headers });
        setTemplate(templateRes.data);
      }
    } catch (error) {
      console.error("Error checking contract status:", error);
      toast.error("Erreur lors du chargement du contrat");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSign = async () => {
    if (!formData.accepte_conditions) {
      toast.error("Vous devez accepter les conditions du contrat");
      return;
    }
    if (!formData.nom_signataire.trim() || !formData.prenom_signataire.trim()) {
      toast.error("Veuillez renseigner votre nom et prénom");
      return;
    }
    
    setSigning(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/contract/sign`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Contrat signé avec succès ! Un email de confirmation vous a été envoyé.");
      setSigned(true);
      setSignatureData({ id: response.data.signature_id });
      
      // Check access status to determine where to redirect
      try {
        const statusRes = await axios.get(`${API_URL}/api/auth/access-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Redirect based on access status
        setTimeout(() => {
          if (statusRes.data.is_blocked) {
            navigate("/blocked");
          } else {
            navigate("/dashboard");
          }
        }, 2000);
      } catch (e) {
        // Default to blocked page if we can't check status
        setTimeout(() => {
          navigate("/blocked");
        }, 2000);
      }
      
    } catch (error) {
      console.error("Error signing contract:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la signature");
    } finally {
      setSigning(false);
    }
  };
  
  const handleDownload = async () => {
    if (!signatureData?.id) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/contract/download/${signatureData.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "contrat_adhesion.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };
  
  const handleSkipToApp = () => {
    navigate("/dashboard");
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }
  
  // Already signed view
  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Contrat signé !
            </h2>
            <p className="text-gray-600 mb-4">
              Votre contrat d'adhésion a été signé avec succès. 
              Un email de confirmation avec le contrat en PDF vous a été envoyé.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Prochaine étape :</strong> Un administrateur va vérifier vos documents et valider votre inscription. 
                Vous serez notifié par email une fois votre compte activé.
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              {signatureData?.id && (
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le contrat PDF
                </Button>
              )}
              <Button
                onClick={handleSkipToApp}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Voir le statut de mon inscription
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Contrat d'Adhésion
          </h1>
          <p className="text-gray-600">
            Veuillez lire et signer le contrat pour finaliser votre inscription
          </p>
        </div>
        
        {/* Contract Content */}
        <Card className="mb-6">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              {template?.title || "Contrat d'Adhésion Food Truck United"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 max-h-[500px] overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              {template?.content ? (
                <ReactMarkdown>{template.content}</ReactMarkdown>
              ) : (
                <p>Chargement du contrat...</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Signature Form */}
        <Card>
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-primary/10">
            <CardTitle className="text-lg">Signature Électronique</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Important</p>
                <p>
                  En signant ce contrat, vous vous engagez à respecter les conditions d'adhésion 
                  et à vous acquitter de la cotisation annuelle de 150€.
                </p>
              </div>
            </div>
            
            {/* Form Fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom_signataire}
                  onChange={(e) => setFormData({ ...formData, prenom_signataire: e.target.value })}
                  placeholder="Votre prénom"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom_signataire}
                  onChange={(e) => setFormData({ ...formData, nom_signataire: e.target.value })}
                  placeholder="Votre nom"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="qualite">En qualité de</Label>
              <Select
                value={formData.qualite_signataire}
                onValueChange={(v) => setFormData({ ...formData, qualite_signataire: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Proprietaire">Propriétaire</SelectItem>
                  <SelectItem value="Gerant">Gérant</SelectItem>
                  <SelectItem value="Associe">Associé</SelectItem>
                  <SelectItem value="Representant">Représentant légal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <Checkbox
                id="accepte"
                checked={formData.accepte_conditions}
                onCheckedChange={(checked) => setFormData({ ...formData, accepte_conditions: checked })}
                className="mt-1"
              />
              <label
                htmlFor="accepte"
                className="text-sm leading-relaxed cursor-pointer"
              >
                <span className="font-semibold">J'accepte les conditions du contrat</span>
                <br />
                <span className="text-gray-600">
                  Je déclare avoir lu et accepté les termes du contrat d'adhésion à l'Association 
                  Food Truck United. Je m'engage à respecter les statuts et le règlement intérieur 
                  de l'Association.
                </span>
              </label>
            </div>
            
            {/* Sign Button */}
            <Button
              onClick={handleSign}
              disabled={signing || !formData.accepte_conditions}
              className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
            >
              {signing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signature en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Signer le contrat
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-gray-500">
              Cette signature électronique a valeur légale conformément au Code civil.
              <br />
              Un email de confirmation avec le contrat signé vous sera envoyé.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
